import { post } from 'aws-amplify/api';
import { AppDispatch } from '../../../store';
import { setProposalStatus } from '../../../store/aiChatSlice';
import { addAssetGroup } from '../../../store/assetGroupSlice';
import { addAssetsToDataGroups } from '../../../store/assetsSlice';

export interface AssetProposalAsset {
  name: string;
  latitude: number;
  longitude: number;
  type: string;
  imageUrl?: string;
  description?: string;
}

export interface AssetProposal {
  assetGroup?: { name: string; description: string; imageUrl?: string };
  // Add-mode only: the existing group name the model believes the user named/confirmed in chat.
  // A hint, not a selection — the frontend resolves it against the user's real groups (exact name,
  // then a unique short-form substring match) and leaves the selection empty (forcing a manual pick)
  // rather than silently defaulting to a different group when nothing matches confidently.
  assetGroupName?: string;
  assets: AssetProposalAsset[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildAssetsDict(assets: AssetProposalAsset[]): Record<string, AssetDictEntry> {
  const dict: Record<string, AssetDictEntry> = {};
  assets.forEach((asset) => {
    const id = crypto.randomUUID();
    dict[id] = { id, name: asset.name, lat: asset.latitude, lng: asset.longitude, description: asset.description ?? asset.type, ...(asset.imageUrl ? { imageUrl: asset.imageUrl } : {}) };
  });
  return dict;
}

// ── Request types ────────────────────────────────────────────────────────────

interface AssetDictEntry {
  id: string;
  name: string;
  lat: number;
  lng: number;
  description: string;
  imageUrl?: string;
}

interface QuickCreateAddBody {
  assetGroup: { id: string };
  assets: Record<string, AssetDictEntry>;
}

interface QuickCreateCreateBody {
  assetGroup: { name?: string; description?: string };
  assets: Record<string, AssetDictEntry>;
}

// ── Response types ───────────────────────────────────────────────────────────

interface QuickCreateResponseAsset {
  id: string;
  name?: string;
  lat?: number;
  lng?: number;
  description?: string;
  imageUrl?: string;
  [key: string]: unknown;
}

interface QuickCreateResponseAssetGroup {
  id: string;
  name?: string;
  description?: string;
  profilePictureUrl?: string;
  assets: { items: QuickCreateResponseAsset[] };
  [key: string]: unknown;
}

interface QuickCreateApiResponse {
  data?: {
    getAssetGroup?: QuickCreateResponseAssetGroup;
  };
}

export async function approveAddProposal(
  proposal: AssetProposal,
  groupId: string,
  messageId: string,
  dispatch: AppDispatch,
  conversationId: string
): Promise<void> {
  const body: QuickCreateAddBody = { assetGroup: { id: groupId }, assets: buildAssetsDict(proposal.assets) };

  const restOperation = post({
    apiName: 'crudopsapi',
    path: '/api/quickcreate',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: { body: body as any }, // Amplify's PostInput body param requires an `any` cast at the SDK boundary
  });
  const response = await restOperation.response;
  const res = await response.body.json() as QuickCreateApiResponse;
  const getAssetGroup = res?.data?.getAssetGroup;
  if (!getAssetGroup) {
    throw new Error(`Unexpected response shape from quickcreate (addProposal): ${JSON.stringify(res)}`);
  }
  const assets = [...getAssetGroup.assets.items];
  dispatch(addAssetsToDataGroups({ assetGroupId: groupId, assets }));
  dispatch(setProposalStatus({ conversationId, messageId, status: 'approved' }));
}

export async function approveProposal(
  proposal: AssetProposal,
  messageId: string,
  dispatch: AppDispatch,
  conversationId: string
): Promise<string> {
  const body: QuickCreateCreateBody = { assetGroup: { name: proposal.assetGroup?.name, description: proposal.assetGroup?.description }, assets: buildAssetsDict(proposal.assets) };

  const restOperation = post({
    apiName: 'crudopsapi',
    path: '/api/quickcreate',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: { body: body as any }, // Amplify's PostInput body param requires an `any` cast at the SDK boundary
  });
  const response = await restOperation.response;
  const res = await response.body.json() as QuickCreateApiResponse;
  const getAssetGroup = res?.data?.getAssetGroup;
  if (!getAssetGroup) {
    throw new Error(`Unexpected response shape from quickcreate (approveProposal): ${JSON.stringify(res)}`);
  }
  const { assets: { items: assetItems }, ...assetGroup } = getAssetGroup;
  const assets = [...assetItems];
  dispatch(addAssetGroup(assetGroup));
  dispatch(addAssetsToDataGroups({ assetGroupId: assetGroup.id, assets }));
  dispatch(setProposalStatus({ conversationId, messageId, status: 'approved' }));
  return assetGroup.id;
}
